import cloneDeep from 'lodash/cloneDeep';
import api from '@/factories/api';
import { fsIdentify } from '@/lib/fullstory';
import { initAblyInPortal } from '@/lib/ably';
import { identifyPortalContact } from '@/lib/analytics';
import { configureTrackJS } from '@/lib/trackjs';
import localStore from 'store';
import router from '@/router';
import EventBus from '@/factories/eventBus';

export default {
  async loginPortal({ commit, getters }, data) {
    if (getters.profileLoading) return;

    commit('setProfileLoading', true);
    try {
      const res = await api().post('cg/auth', data);
      if (!res?.data?.access_token) return res;

      const profile = res.data;
      commit('setProfilePid', profile.pid);
      commit('setProfile', profile);

      const authPayload = {
        id: getters.profilePid,
        token: profile.access_token,
      };

      const testingPid = localStore.get('portal_testing_pid');
      if (testingPid && testingPid === getters.profilePid) {
        localStore.set(`auth_${testingPid}`, authPayload);
      } else {
        localStore.set('portal_auth', authPayload);
      }

      initAblyInPortal(profile);
      syncProfileIdentity({
        profile,
        profilePid: getters.profilePid,
      });

      return profile;
    } catch (error) {
      const testingPid = localStore.get('portal_testing_pid');
      if (testingPid) {
        localStore.remove(`auth_${testingPid}`);
      } else {
        localStore.remove('portal_auth');
      }
      throw error;
    } finally {
      commit('setProfileLoading', false);
    }
  },

  async resetScormCourse(_, campaign_id) {
    try {
      const path = 'plugin/scorm/campaignprogress/resetCampaignProgress';
      const res = await api({ baseURL: '/' }).post(path, {
        campaign_id,
      });
    } catch (error) {
      console.log('failed to reset scorm campaign', error);
    }
  },

  async loginScormLearner({ commit, getters, dispatch }, params) {
    const { learnerId, learnerName, resetProgress, courseId, metadata } =
      params;

    commit('setProfileLoading', true);
    commit('setScormParameters', { courseId, learnerId, resetProgress });

    try {
      // login
      const res = await api({ baseURL: '/' }).post('plugin/scorm/contacts', {
        learner_id: learnerId,
        name: learnerName,
        metadata,
      });

      const { contact_pid, token } = res || {};
      if (!contact_pid || !token) return;

      localStore.set('portal_auth', { id: contact_pid, token });
      commit('setProfilePid', contact_pid);

      // load profile info
      await dispatch('loadProfile');

      // join scorm course
      await dispatch('joinScormCourse');
    } catch (error) {
      throw error;
    } finally {
      commit('setProfileLoading', false);
    }
  },

  async joinScormCourse({ state, dispatch }) {
    try {
      const { learnerId, courseId, resetProgress } = state.scormParameters;

      const res = await api({ baseURL: '/' }).post(
        'plugin/scorm/contacts/campaign',
        {
          learner_id: learnerId,
          campaign_id: courseId,
        }
      );

      // if campaign_joined is false, it means the learner has already joined the course
      if (resetProgress && res?.campaign_joined === false) {
        await dispatch('resetScormCourse', courseId);
      }

      router.push({
        name: 'portal-course',
        params: { id: courseId },
      });
    } catch (error) {
      console.log('failed to join scorm course', error);
    }
  },

  async loadProfile({ commit, getters }, { hideLoading = false } = {}) {
    if (!hideLoading) {
      commit('setProfileLoading', true);
    }

    try {
      const profile = await api().get('cg/info?schema=full');
      commit('setProfile', profile);
      syncProfileIdentity({
        profile,
        profilePid: getters.profilePid,
      });
    } finally {
      commit('setProfileLoading', false);
    }
  },

  async updateProfile({ commit, getters }, updates) {
    commit('setProfileUpdating', true);
    try {
      const profile = await api().put('cg/info', updates);
      commit('setProfile', cloneDeep(profile));
      // Same turn as setProfile, before the profile watcher reads
      // custom_record_ids. The ids stay on profileFields if the refresh fails.
      commit('patchProfileFieldRecordIds', updates);
      syncProfileIdentity({
        profile,
        profilePid: getters.profilePid,
      });
    } finally {
      commit('setProfileUpdating', false);
    }
  },

  async getNextRoute({}, hash) {
    try {
      return await api().get('cg/next?hash=' + hash);
    } catch (error) {
      throw error;
    }
  },

  async fetchUserRole({ commit }) {
    try {
      const role = await api().get('contacts/role');
      commit('setRole', role);
    } catch (error) {
      commit('setRole', null);
    }
  },

  showScormMergeVerification(_, { email, sent }) {
    const displayEmail = email || 'your registered email';
    EventBus.$emit('openConfirmationModal', {
      title: 'Check Your Inbox',
      text: sent
        ? `We sent a verification email to ${displayEmail}. Open the email to transfer your course progress to your existing account.`
        : `We couldn't send a verification email to ${displayEmail}. Please try again or contact support.`,
      buttonText: 'OK',
      hideCancelButton: true,
    });
  },

  async fetchProfileFields({ commit, state }, { force = false } = {}) {
    if (state.profileFields && !force) return;

    const fields = await api().get('cg/fields');
    commit('setProfileFields', fields);
  },
};

const syncProfileIdentity = ({ profile, profilePid }) => {
  configureTrackJS(profilePid);
  fsIdentify({
    id: profilePid,
    data: profile,
  });
  identifyPortalContact(profile);
};
